import React, { type ReactElement } from 'react';
import {
  Button,
  Icon,
  Notice,
  Link as CauldronLink
} from '@deque/cauldron-react';
import { Trans, useTranslation } from 'react-i18next';
import { ProductSlugs } from '@deque/billing-utils';
import WalkthroughContent from './WalkthroughContent';
import { useProducts } from '../../common/contexts/products';
import styles from './ProWalkthrough.css';

const Plus = (): ReactElement => <span>&nbsp;+&nbsp;</span>;

interface Props {
  isMac: boolean;
  awaitingMessage: boolean;
  completedSteps: number;
  onNextClick: () => void;
}

const extensionTabName = 'axe DevTools';

export const OpenDevTools = ({
  isMac,
  awaitingMessage,
  completedSteps,
  onNextClick
}: Props): ReactElement => {
  const { t } = useTranslation();
  const keyboardFocusShift = isMac ? (
    <Trans>
      <kbd>command</kbd>
      <Plus />
      <kbd>option</kbd>
      <Plus />
      <kbd>down arrow</kbd>
    </Trans>
  ) : (
    <Trans>
      <kbd>ctrl</kbd>
      <Plus />
      <kbd>shift</kbd>
      <Plus />
      <kbd>down arrow</kbd>
    </Trans>
  );
  const keyboardOpenDevtools = isMac ? (
    <Trans>
      <kbd>command</kbd>
      <Plus />
      <kbd>option</kbd>
      <Plus />
      <kbd>i</kbd>
    </Trans>
  ) : (
    <Trans>
      <kbd>ctrl</kbd>
      <Plus />
      <kbd>shift</kbd>
      <Plus />
      <kbd>i</kbd>
    </Trans>
  );

  return (
    <WalkthroughContent
      active={completedSteps === 0}
      title={t('Open {{ extensionTabName }}', {
        extensionTabName
      })}
      content={
        <>
          <Notice type="info" title={<h3>{t('Notice for Keyboard Users')}</h3>}>
            <p>
              <Trans>
                For keyboard users, during testing, you may need to shift focus
                between the devtools extension and the page being tested. There
                are 2 recommended approaches to doing so:
              </Trans>
            </p>
            <ul>
              <li>
                <Trans>
                  Using the keyboard shortcut to cycle focus between devtools
                  and the page being tested:{' '}
                </Trans>
                {keyboardFocusShift}
              </li>
              <li>
                <Trans shouldUnescape>
                  Undocking devtools by clicking &quot;Customize and control
                  DevTools&quot; and then &quot;Undock into separate
                  window&quot;. From there use your operating system’s shortcut
                  to cycle between windows.
                </Trans>
              </li>
            </ul>
          </Notice>
          <ol className={styles.orderedList}>
            <li>
              <div>
                <Trans i18nKey="Open Developer Tools in <1>current tab</1>">
                  Open Developer Tools in <strong>current tab</strong>
                </Trans>
              </div>
              <div>
                <Trans>
                  To open developer tools press the following key command:{' '}
                </Trans>
                {keyboardOpenDevtools}
              </div>
            </li>
            <li>
              <Trans>
                Select the <strong>{{ extensionTabName }}</strong> tab
              </Trans>
            </li>
          </ol>
        </>
      }
      statusMessage={
        awaitingMessage ? (
          <>
            <Icon type={'clock'} />
            <div>
              {t('Waiting for {{extensionTabName}} extension to be opened...', {
                extensionTabName
              })}
            </div>
            <Button variant="link" onClick={onNextClick}>
              {t('I’ve already opened the {{extensionTabName}} extension', {
                extensionTabName
              })}
            </Button>
          </>
        ) : (
          <>
            <Icon type={'check-circle'} />
            <div>{t('Extension opened successfully!')}</div>
          </>
        )
      }
    />
  );
};

export const RunScan = ({
  awaitingMessage,
  completedSteps,
  onNextClick
}: Props): ReactElement => {
  const { t } = useTranslation();

  return (
    <WalkthroughContent
      active={completedSteps === 1}
      title={t('Run a Scan')}
      content={
        <Trans i18nKey="<0>Let’s run a quick automated scan by clicking the <2>Full Page Scan</2> button.</0>">
          <p>
            Let’s run a quick automated scan by clicking the{' '}
            <strong>Full Page Scan</strong> button.
          </p>
        </Trans>
      }
      statusMessage={
        awaitingMessage ? (
          <>
            <Icon type={'clock'} />
            <div>{t('Waiting for scan to be performed...')}</div>
            <Button variant="link" onClick={onNextClick}>
              {t('I’ve already performed the scan')}
            </Button>
          </>
        ) : (
          <>
            <Icon type={'check-circle'} />
            <div>{t('Scan performed successfully!')}</div>
          </>
        )
      }
    />
  );
};

export const SaveTest = ({
  awaitingMessage,
  completedSteps,
  onNextClick
}: Props): ReactElement => {
  const { t } = useTranslation();

  return (
    <WalkthroughContent
      active={completedSteps === 2}
      title={t('Save Test')}
      content={
        <p>
          <Trans shouldUnescape>
            Click the
            <Icon type="save" />
            &quot;SAVE TEST&quot; button and give your test a name. This will
            create a persistent test record for the scan you just ran and allow
            you to run Intelligent Guided Tests!
          </Trans>
        </p>
      }
      statusMessage={
        awaitingMessage ? (
          <>
            <Icon type={'clock'} />
            <div>{t('Waiting for test to be saved...')}</div>
            <Button variant="link" onClick={onNextClick}>
              {t('I’ve already saved the test')}
            </Button>
          </>
        ) : (
          <>
            <Icon type={'check-circle'} />
            <div>{t('Test saved successfully!')}</div>
          </>
        )
      }
    />
  );
};

export const FreeUserSaveTest = ({ completedSteps }: Props): ReactElement => {
  const { t } = useTranslation();

  return (
    <WalkthroughContent
      active={completedSteps === 2}
      title={t('Walkthrough Complete')}
      content={null}
      statusMessage={
        <div className={styles.freeComplete}>
          <div>
            <Icon type={'check-circle'} />
          </div>
          <p>
            {t(
              'You have completed the {{ extensionTabName }} Browser Extension walkthrough. Upgrade to Pro to complete the remaining steps! Try a free trial. No credit card required.',
              extensionTabName
            )}
          </p>
          <ul>
            <li>
              {t(
                'Unlock the full power of the best accessibility testing browser extension'
              )}
            </li>
            <li>
              {t(
                'AI-powered tests save time and provide more coverage than any other tool'
              )}
            </li>
            <li>
              {t(
                'Save your progress, export, and share reports with one click'
              )}
            </li>
          </ul>
        </div>
      }
    />
  );
};

export const CompleteIGT = ({
  awaitingMessage,
  completedSteps,
  onNextClick
}: Props) => {
  const { t } = useTranslation();

  return (
    <WalkthroughContent
      active={completedSteps === 3}
      title={t('Complete an IGT')}
      content={
        <>
          <p>
            <Trans shouldUnescape>
              Click the &quot;Guided Tests&quot; tab and click the
              <Icon type="plus" /> start button for any of the available
              Intelligent Guided Tests.
            </Trans>
          </p>
          <p>
            {t(
              'Intelligent Guided Tests (IGTs) will ask you a series of simple questions about the page you are testing. Based on your answers, {{ extensionTabName }} will automatically raise accessibility issues for you.',
              {
                extensionTabName
              }
            )}
          </p>
        </>
      }
      statusMessage={
        awaitingMessage ? (
          <>
            <Icon type={'clock'} />
            <div>{t('Waiting for IGT to be completed...')}</div>
            <Button variant="link" onClick={onNextClick}>
              {t('I’ve already completed an IGT')}
            </Button>
          </>
        ) : (
          <>
            <Icon type={'check-circle'} />
            <div>{t('IGT completed!')}</div>
          </>
        )
      }
    />
  );
};

export const WalkthroughCompleted = ({ completedSteps }: Props) => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();
  const productName =
    getProductBySlug(ProductSlugs.axeDevToolsExtension)?.name ||
    'axe DevTools Extension';

  return (
    <WalkthroughContent
      active={completedSteps === 4}
      title={t('Walkthrough completed!')}
      content={
        <>
          <p>
            {t(
              'Congratulations! You have just completed the {{ productName }} walkthrough.',
              {
                productName
              }
            )}{' '}
            <Trans>
              Now is a great time to explore other Pro features such as{' '}
              <CauldronLink
                href="https://docs.deque.com/devtools-html/4.0.0/en/devtools-export"
                target="_blank"
                rel="noopener noreferrer"
              >
                exporting
              </CauldronLink>
              ,{' '}
              <CauldronLink
                href="https://docs.deque.com/devtools-html/4.0.0/en/devtools-share"
                target="_blank"
                rel="noopener noreferrer"
              >
                sharing
              </CauldronLink>
              ,{' '}
              <CauldronLink
                href="https://docs.deque.com/devtools-for-web/4/en/devtools-scanning#partial-page-scan"
                target="_blank"
                rel="noopener noreferrer"
              >
                component-level testing
              </CauldronLink>{' '}
              and more!
            </Trans>
          </p>
        </>
      }
      statusMessage={
        <>
          <Icon type={'check-circle'} />
          <div>{t('Walkthrough completed!')}</div>
        </>
      }
    />
  );
};
